// ============================================================================
// ORG LOOKUP SERVICE (accounting entities / business units)
// ============================================================================
// Read-only lookups over org.tbl_entity / tbl_business_unit — the accounting
// scope pickers on the Property master. Served at /org/pickers/* — /org itself
// is the Org Admin module (organisationService.ts), which maintains the data.

import { get } from '../client/axiosClient';
import { buildEndpoint, buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse } from '../types';

export interface OrgEntityLookup {
  id: string;
  entityCode: string;
  entityName: string;
}

export interface OrgBusinessUnitLookup {
  id: string;
  entityId: string;
  unitCode: string;
  unitName: string;
  parentUnitId?: string | null;
  unitTypeCode?: string | null;
  unitTypeName?: string | null;
  /** Names from the top unit down, e.g. "Hotel Munnar › Food & Beverage › Garden Café". */
  path: string;
  /** The asked property's unit or one under it; such units come first. */
  inProperty: boolean;
}

/** Narrowing for the business-unit picker. Only active units are ever returned. */
export interface OrgBusinessUnitFilter {
  /** Unit type codes, e.g. ['DEPARTMENT', 'COST_CENTER']. */
  types?: string[];
  /** List this property's unit and the units under it first. */
  propertyId?: string;
}

class OrgService {
  async getEntities(): Promise<ApiResponse<OrgEntityLookup[]>> {
    const response = await get<ApiResponse<OrgEntityLookup[]>>(buildEndpoint('/org/pickers/entities'));
    return response.data;
  }

  async getBusinessUnits(entityId?: string, filter?: OrgBusinessUnitFilter): Promise<ApiResponse<OrgBusinessUnitLookup[]>> {
    const query: Record<string, string> = {};
    if (entityId) query.entityId = entityId;
    if (filter?.types?.length) query.types = filter.types.join(',');
    if (filter?.propertyId) query.propertyId = filter.propertyId;
    const endpoint = Object.keys(query).length
      ? buildEndpointWithQuery('/org/pickers/business-units', query)
      : buildEndpoint('/org/pickers/business-units');
    const response = await get<ApiResponse<OrgBusinessUnitLookup[]>>(endpoint);
    return response.data;
  }
}

export const orgService = new OrgService();
export default orgService;
